import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Download,
  X,
  Clock,
  Bell,
  Calendar,
  Send,
  Settings,
  Edit,
  Upload,
  XCircle,
  CheckCircle,
} from 'lucide-react';

interface ExpiryInfo {
  status: string;
  color: string;
  label: string;
}

interface DocumentData {
  id: string;
  employeeName: string;
  employeeId: string;
  department: string;
  documentType: string;
  uploadDate: string;
  expiryDate: string;
  status: string;
  fileSize: string;
  fileName: string;
  uploadedBy: string;
  notifications?: boolean;
}

interface DocumentPreviewModalProps {
  document: DocumentData;
  onClose: () => void;
  getExpiryStatus: (expiryDate: string) => ExpiryInfo;
}

const DocumentPreviewModal = ({ document, onClose, getExpiryStatus }: DocumentPreviewModalProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('details');

  const expiryInfo = getExpiryStatus(document?.expiryDate);

  const formatDate = (dateString: string) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatFileSize = (size: string) => {
    return size;
  };

  const handleDownload = () => {
    console.log('Downloading document:', document?.fileName);
  };

  const handleNotification = () => {
    console.log('Sending notification for:', document?.id);
  };

  const handleEmployeeProfile = () => {
    console.log('Navigate to employee profile:', document?.employeeId);
  };

  const documentHistory = [
    {
      id: '1',
      action: 'Document uploaded',
      date: document?.uploadDate,
      user: document?.uploadedBy,
      details: 'Initial document upload'
    },
    {
      id: '2',
      action: 'Notification sent',
      date: '2024-02-05',
      user: 'System',
      details: 'Expiry reminder notification sent to employee'
    }
  ];

  const tabs = [
    { id: 'details', label: t('Details'), icon: FileText },
    { id: 'history', label: t('History'), icon: Clock },
    { id: 'notifications', label: t('Notifications'), icon: Bell }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-gray-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{document?.documentType}</h2>
              <p className="text-sm text-gray-500">
                {document?.employeeName} • {document?.employeeId}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>{t('Download')}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs?.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-3 border-b-2 text-sm font-medium transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-border'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  <span>{tab?.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* Document Preview */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Document Preview')}</h3>
                <div className="border border-border rounded-lg p-8 text-center bg-gray-50">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">{document?.fileName}</p>
                  <p className="text-sm text-gray-500">
                    {t('Preview for')} {document?.documentType} {t('would be displayed here')}
                  </p>
                </div>
              </div>

              {/* Document Information */}
              <div className="space-y-6">

                {/* Employee Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('Employee Information')}</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('Name')}:</span>
                      <button
                        onClick={handleEmployeeProfile}
                        className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                      >
                        {document?.employeeName}
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('Employee ID')}:</span>
                      <span className="text-sm font-medium">{document?.employeeId}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('Department')}:</span>
                      <span className="text-sm font-medium">{document?.department}</span>
                    </div>
                  </div>
                </div>

                {/* Document Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('Document Details')}</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('Type')}:</span>
                      <span className="text-sm font-medium">{document?.documentType}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('File Name')}:</span>
                      <span className="text-sm font-medium">{document?.fileName}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('File Size')}:</span>
                      <span className="text-sm font-medium">{formatFileSize(document?.fileSize)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('Upload Date')}:</span>
                      <span className="text-sm font-medium">{formatDate(document?.uploadDate)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('Uploaded By')}:</span>
                      <span className="text-sm font-medium">{document?.uploadedBy}</span>
                    </div>
                  </div>
                </div>

                {/* Expiry Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('Expiry Information')}</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('Expiry Date')}:</span>
                      <span className="text-sm font-medium">{formatDate(document?.expiryDate)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('Status')}:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${expiryInfo?.color}`}>
                        {expiryInfo?.status === 'expired' && <XCircle className="w-3 h-3 mr-1" />}
                        {expiryInfo?.status === 'expiring' && <Clock className="w-3 h-3 mr-1" />}
                        {expiryInfo?.status === 'active' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {expiryInfo?.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('Notifications')}:</span>
                      <span className={`text-sm font-medium ${document?.notifications ? 'text-green-600' : 'text-gray-500'}`}>
                        {document?.notifications ? t('Enabled') : t('Disabled')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-border">
                  <div className="space-y-2">
                    <button
                      onClick={handleNotification}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
                    >
                      <Bell className="w-4 h-4" />
                      <span>{t('Send Expiry Notification')}</span>
                    </button>
                    <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                      <Edit className="w-4 h-4" />
                      <span>{t('Edit Document Details')}</span>
                    </button>
                    <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                      <Upload className="w-4 h-4" />
                      <span>{t('Upload New Version')}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Document History')}</h3>
              <div className="space-y-4">
                {documentHistory?.map((entry) => (
                  <div key={entry?.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Clock className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-gray-900">{entry?.action}</h4>
                        <span className="text-xs text-gray-500">{formatDate(entry?.date)}</span>
                      </div>
                      <p className="text-sm text-gray-600">{entry?.details}</p>
                      <p className="text-xs text-gray-500 mt-1">{t('by')} {entry?.user}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Notification Settings')}</h3>
              <div className="space-y-6">

                {/* Current Settings */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-900">{t('Expiry Notifications')}</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={document?.notifications}
                        className="sr-only peer"
                        readOnly
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    {document?.notifications
                      ? t('Employee will receive notifications about document expiry')
                      : t('No notifications will be sent for this document')
                    }
                  </p>
                </div>

                {/* Notification Schedule */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">{t('Notification Schedule')}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-gray-600">{t('90 days before expiry')}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-gray-600">{t('30 days before expiry')}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-gray-600">{t('7 days before expiry')}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-gray-600">{t('On expiry date')}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={handleNotification}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    <span>{t('Send Notification Now')}</span>
                  </button>
                  <button className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                    <Settings className="w-4 h-4" />
                    <span>{t('Configure Settings')}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
